<template>
    <ajax-form-modal v-model="isActive"
                     :form-options="{action: props.job ? `/v1/jobs/${props.job.id}/applications` : (jobDetails.role_id ? `/v1/jobs/${jobDetails.role_id}/applications` : null), data: data, method: 'POST'}"
                     :modal-options="{title: props.title ?? `${jobDetails.application_label?.title ?? $t('action.apply')}: ${jobDetails.title}`, subtitle: props.group.display_name}"
                     :submit-text="props.title ?? (jobDetails.application_label?.title ?? $t('action.apply'))"
                     :hide-submit="props.posting && !jobDetails.is_managed"
                     @success="createdApplication => $emit('success', createdApplication)">

        <fetch-loader v-if="pending || inputsPending || error" :pending="pending || inputsPending" :error="error" @retry="refresh" height="200"/>
        <user-content v-else-if="props.posting && !jobDetails.is_managed" :text="jobDetails.apply_description ?? '-'"/>
        <template v-else>
            <template v-if="!$auth.loggedIn && data.account">
                <p class="subheading">{{ $t('action.create_free_account') }}</p>
                <p class="subheading-description">
                    <span>{{ $t('text.job_application.create_free_account', {job: jobDetails.title}) }}</span>
                    <nuxt-link :to="$spoferanUrl(`/login?redirect=${$route.fullPath}`)">{{ $t('action.login') }}</nuxt-link>
                </p>
                <lazy-account-registration-form-inputs :data="data.account" name-prefix="account.">
                    <column v-if="showBirthdayInputs" cols="12" :md="showGenderInputs ? 6 : 12">
                        <lazy-form-native-date-input v-model="data.birthday" name="birthday"
                                                :rules="{max: $date().format('YYYY-MM-DD'), required: jobDetails.address_input === 'required'}"
                                                :clearable="jobDetails.address_input !== 'required'"
                                                :label="$t('label.birthday')"/>
                    </column>
                    <column v-if="showGenderInputs" cols="12" :md="showBirthdayInputs ? 6 : 12">
                        <lazy-form-gender-select v-model="data.gender" name="gender"
                                            :rules="{required: jobDetails.gender_input === 'required'}"
                                            :clearable="jobDetails.gender_input !== 'required'"
                                            :label="$t('label.gender')"/>
                    </column>

                    <column v-if="showPhoneInputs" cols="12">
                        <form-input v-model="data.phone" type="tel"
                                    :rules="{required: jobDetails.phone_input === 'required', min: 5, max: 30, phone: true}"
                                    placeholder="+49"
                                    name="phone"
                                    :label="$t('label.phone')">
                        </form-input>
                    </column>
                </lazy-account-registration-form-inputs>
                <divider class="mt-16"/>
            </template>

            <row v-if="$auth.loggedIn && (showGenderInputs || showPhoneInputs || showBirthdayInputs)">
                <column v-if="showBirthdayInputs" cols="12" :md="showGenderInputs ? 6 : 12">
                    <lazy-form-native-date-input v-model="data.birthday" name="birthday"
                                            :rules="{max: $date().format('YYYY-MM-DD'), required: jobDetails.address_input === 'required'}"
                                            :clearable="jobDetails.address_input !== 'required'"
                                            :label="$t('label.birthday')"/>
                </column>
                <column v-if="showGenderInputs" cols="12" :md="showBirthdayInputs ? 6 : 12">
                    <lazy-form-gender-select v-model="data.gender" name="gender"
                                        :rules="{required: jobDetails.gender_input === 'required'}"
                                        :clearable="jobDetails.gender_input !== 'required'"
                                        :label="$t('label.gender')"/>
                </column>

                <column v-if="showPhoneInputs" cols="12">
                    <form-input v-model="data.phone" type="tel"
                                :rules="{required: jobDetails.phone_input === 'required', min: 5, max: 30, phone: true}"
                                placeholder="+49"
                                name="phone"
                                :label="$t('label.phone')">
                    </form-input>
                </column>

            </row>

            <template v-if="showAddressInputs">
                <p class="subheading mb-20">{{ $t('label.your_address') }}</p>
                <lazy-contact-form-inputs :data="data" :required="jobDetails.address_input === 'required'" :street-required="jobDetails.address_input === 'required'" slim/>
            </template>

            <template v-if="inputs.length && data.inputs">
                <p class="subheading mb-20">{{ $t('label.advanced_information') }}</p>
                <custom-form-inputs :data="data.inputs" :inputs="inputs"/>
            </template>

            <divider v-if="showAddressInputs || (inputs.length && data.inputs)" class="mt-16"/>

            <form-textarea name="description"
                           v-model="data.description"
                           :label="$t('label.message')"
                           rows="10"
                           :placeholder="$t('help.job_application.description', {group: props.group.display_name})"
                           :help="$t('help.job_application.description', {group: props.group.display_name})"/>
        </template>
    </ajax-form-modal>
</template>

<script setup lang="ts">
import {useToggleableComponent} from "../../composables/useToggleableComponent";
import type {ClubPreview, EventPreview, JobPreview} from "@spoferan/spoferan-ts-core";
import {ToggleableComponentEmits, type ToggleableComponentProps} from "../../types";
import {computed, ref, watch} from 'vue';
import {useNuxtApp, useApiFetch} from '#imports';

const props = defineProps<{
	// The group the posting belongs to
	group: EventPreview | ClubPreview,

	// The job posting to apply for. If not specified, the 'job' props must be set.
	posting?: JobPreview,

	// The job to apply for. If not specified, the 'posting' props must be set.
	job?: JobPreview,

	// A custom modal title and submit button text to use instead of the posting title and application label
	title?: string
} & ToggleableComponentProps>();

const emit = defineEmits([...ToggleableComponentEmits, 'success']);

const {isActive} = useToggleableComponent(props, emit);
const {$auth} = useNuxtApp();

const requiresDetails = computed(() => {
	return props.posting && (props.posting.apply_description === undefined || props.posting.address_input === undefined)
});

const requiresInputs = computed(() => {
	return props.job || (props.posting && props.posting.is_managed && props.posting.role_id);
});

const apiPath = computed(() => {
	return `/v1/${props.group.model_name}s/${props.group.id}/job-postings/${props.posting!.id}`
});

const {reactiveData: serverJobData, pending, error, refresh} = requiresDetails.value ? (await useApiFetch(apiPath, {
	auth: true,
	lazy: true
})) : {
	reactiveData: ref(null),
	pending: false,
	error: null,
	refresh: null
};

const jobDetails = computed(() => {
	return requiresDetails.value && serverJobData.value ? serverJobData.value : (props.job ?? props.posting);
});

const {reactiveData: inputs, pending: inputsPending} = requiresInputs.value ? (await useApiFetch(`/v1/jobs/${props.job ? props.job.id : props.posting.role_id}/inputs`, {
	lazy: true,
	params: {
		relations: ['selectOptions', 'conditions'],
		for_users: true,
		user_id: $auth.id
	}
})) : {
	reactiveData: ref([])
};

const data = ref({account: {}, inputs: {}});

const showAddressInputs = computed(() => {
	return jobDetails.value.address_input !== 'hidden' && (!$auth.user || !$auth.user.street);
});

const showGenderInputs = computed(() => {
	return jobDetails.value.gender_input !== 'hidden' && (!$auth.user || !$auth.user.gender);
});

const showBirthdayInputs = computed(() => {
	return jobDetails.value.birthday_input !== 'hidden' && (!$auth.user || !$auth.user.birthday);
});

const showPhoneInputs = computed(() => {
	return jobDetails.value.phone_input !== 'hidden' && (!$auth.user || !$auth.user.phone);
});

watch(isActive, (newValue) => {
	if (newValue) {
		initInputsFormData();
		initProfileFormData();
	}
});

watch(jobDetails, () => {
	initProfileFormData();
});

watch(inputs, () => {
	initInputsFormData();
});

function initInputsFormData() {
	const inputsData = {};
	inputs.value.map(input => {
		inputsData[input.id] = {id: input.id, value: input.pivot?.value ?? input.default_value};
	});

	data.value['inputs'] = inputsData;
}

function initProfileFormData() {
	if (!jobDetails.value || !jobDetails.value.is_managed) {
		delete data.value['gender'];
		delete data.value['birthday'];
		delete data.value['phone'];
		delete data.value['country_code'];
		delete data.value['state_code'];
		delete data.value['postcode'];
		delete data.value['city'];
		delete data.value['street'];
	} else {
		if (jobDetails.value.gender_input !== 'hidden') {
			data.value['gender'] = $auth.user ? $auth.user.gender : null;
		} else {
			delete data.value['gender'];
		}
		if (jobDetails.value.phone_input !== 'hidden') {
			data.value['phone'] = $auth.user ? $auth.user.phone : null;
		} else {
			delete data.value['phone'];
		}
		if (jobDetails.value.birthday_input !== 'hidden') {
			data.value['birthday'] = $auth.user ? $auth.user.birthday : null;
		} else {
			delete data.value['birthday'];
		}
		if (jobDetails.value.address_input !== 'hidden') {
			data.value['country_code'] = $auth.user ? $auth.user.country_code : null;
			data.value['state_code'] = $auth.user ? $auth.user.state_code : null;
			data.value['postcode'] = $auth.user ? $auth.user.postcode : null;
			data.value['city'] = $auth.user ? $auth.user.city : null;
			data.value['street'] = $auth.user ? $auth.user.street : null;
		} else {
			delete data.value['country_code'];
			delete data.value['state_code'];
			delete data.value['postcode'];
			delete data.value['city'];
			delete data.value['street'];
		}
	}
}
</script>